import { takeEvery, call, put, select } from 'redux-saga/effects';
import request from 'utils/request';
import { removeWhitespace, toUpperCase } from 'utils/stringMutation';
import { getErrorMessage } from 'utils/error';

// Actions
const INPUT_CHANGE = 'valuations/valuation/INPUT_CHANGE';
const LOAD_CAR = 'valuations/valuation/LOAD_CAR';
const LOAD_CAR_SUCCEEDED = 'valuations/valuation/LOAD_CAR_SUCCEEDED';
const LOAD_CAR_FAILED = 'valuations/valuation/LOAD_CAR_FAILED';
const LOAD_VALUATION = 'valuations/valuation/LOAD_VALUATION';
const LOAD_VALUATION_SUCCEEDED =
  'valuations/valuation/LOAD_VALUATION_SUCCEEDED';
const LOAD_VALUATION_FAILED = 'valuations/valuation/LOAD_VALUATION_FAILED';
const RESET_CAR_DETAILS = 'valuations/valuation/RESET_CAR_DETAILS';
const SET_OEM_DETAILS = 'valuations/valuation/SET_OEM_DETAILS';

const initialState = {
  carDetails: {
    colour: '',
    derivative: '',
    manufacturer: '',
    model: '',
    year: '',
    firstRegistrationDate: '',
    derivativeId: '',
    averageValuation: {}
  },
  oemDetails: {},
  formValues: {
    condition: 'average',
    salutation: '',
    firstName: '',
    lastName: '',
    intent: '',
    mileage: '',
    phone: '',
    email: '',
    postcode: '',
    marketingPermission: false,
    vrm: '',
    desiredMake: '',
    desiredModel: '',
    campaignOptIn: ''
  },
  isLoading: false,
  isVrmValid: null,
  valuation: {
    priv: {},
    px: {},
    trade: {}
  }
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case INPUT_CHANGE:
      return {
        ...state,
        formValues: {
          ...state.formValues,
          [action.name]: action.value
        },
        isVrmValid: null
      };

    case LOAD_CAR:
      return {
        ...state,
        isLoading: true
      };

    case LOAD_CAR_FAILED:
      return {
        ...state,
        isLoading: false,
        isVrmValid: false
      };

    case LOAD_CAR_SUCCEEDED:
      return {
        ...state,
        carDetails: {
          colour: action.car.colour,
          derivative: action.car.derivative,
          derivativeId: action.car.derivativeId,
          manufacturer: action.car.make,
          model: action.car.model,
          year:
            action.car.firstRegistrationDate &&
            new Date(action.car.firstRegistrationDate).getFullYear(),
          firstRegistrationDate: action.car.firstRegistrationDate,
          averageValuation: action.averageValuation
        },
        isLoading: false,
        isVrmValid: true
      };

    case LOAD_VALUATION:
      return {
        ...state,
        isLoading: true
      };

    case LOAD_VALUATION_FAILED:
      return {
        ...state,
        isLoading: false
      };

    case LOAD_VALUATION_SUCCEEDED:
      return {
        ...state,
        valuation: {
          priv: action.valuation.priv,
          px: action.valuation.px,
          trade: action.valuation.trade
        },
        isLoading: false
      };

    case RESET_CAR_DETAILS:
      return {
        carDetails: { ...initialState.carDetails },
        oemDetails: {},
        formValues: { ...initialState.formValues },
        isVrmValid: null,
        isLoading: false,
        valuation: {
          priv: {},
          px: {},
          trade: {}
        }
      };

    case SET_OEM_DETAILS:
      return {
        ...state,
        oemDetails: action.value
      };

    default:
      return state;
  }
}

// Action Creators
export function fetchCar() {
  return { type: LOAD_CAR };
}

export function fetchValuation() {
  return { type: LOAD_VALUATION };
}

export function inputChange(name, value) {
  return { type: INPUT_CHANGE, name, value };
}

export function setOemDetails(value) {
  return { type: SET_OEM_DETAILS, value };
}

export function resetCarDetails() {
  return { type: RESET_CAR_DETAILS };
}

// Sagas
export function* fetchCarSaga() {
  yield takeEvery(LOAD_CAR, getCar);
}

export function* getCar() {
  const state = yield select();

  const params = {
    vrm: removeWhitespace(toUpperCase(state.valuation.formValues.vrm)),
    mileage: removeWhitespace(state.valuation.formValues.mileage)
  };

  try {
    const axiosRequest = yield request();
    const response = yield call(axiosRequest, {
      method: 'GET',
      url: '/car',
      params
    });
    const car = response.data.results && response.data.results.vehicle;
    const valuation = response.data.results && response.data.results.valuations;
    yield put({
      type: LOAD_CAR_SUCCEEDED,
      car,
      averageValuation: valuation
    });
  } catch (e) {
    const message = getErrorMessage(e);
    yield put({ type: LOAD_CAR_FAILED, message });
  }
}

export function* fetchValuationSaga() {
  yield takeEvery(LOAD_VALUATION, getValuation);
}

export function* getValuation() {
  const state = yield select();
  const params = {
    salutation: state.valuation.formValues.salutation,
    firstname: removeWhitespace(state.valuation.formValues.firstName),
    lastname: removeWhitespace(state.valuation.formValues.lastName),
    intent: removeWhitespace(state.valuation.formValues.intent),
    mileage: removeWhitespace(state.valuation.formValues.mileage),
    phone: removeWhitespace(state.valuation.formValues.phone),
    email: removeWhitespace(state.valuation.formValues.email),
    marketingPermission: state.valuation.formValues.marketingPermission,
    vrm: removeWhitespace(toUpperCase(state.valuation.formValues.vrm)),
    colour: removeWhitespace(state.valuation.carDetails.colour),
    year: state.valuation.carDetails.year,
    desiredMake: state.valuation.formValues.desiredMake,
    desiredModel: state.valuation.formValues.desiredModel,
    valuedMake: state.valuation.carDetails.manufacturer,
    valuedModel: state.valuation.carDetails.model,
    zutoOptIn: state.valuation.formValues.zutoOptIn,
    campaignOptIn: state.valuation.formValues.campaignOptIn,
    firstRegistrationDate: state.valuation.carDetails.firstRegistrationDate,
    derivativeId: state.valuation.carDetails.derivativeId,
    derivative: state.valuation.carDetails.derivative
  };

  try {
    const axiosRequest = yield request();
    const response = yield call(axiosRequest, {
      method: 'GET',
      url: '/valuation',
      params
    });

    const fullValuation = response.data.results;
    fullValuation.priv.average =
      state.valuation.carDetails.averageValuation.private &&
      state.valuation.carDetails.averageValuation.private.amountGBP !==
        undefined
        ? state.valuation.carDetails.averageValuation.private.amountGBP.toString()
        : '0';
    fullValuation.px.average =
      state.valuation.carDetails.averageValuation.partExchange &&
      state.valuation.carDetails.averageValuation.partExchange.amountGBP !==
        undefined
        ? state.valuation.carDetails.averageValuation.partExchange.amountGBP.toString()
        : '0';
    fullValuation.trade.average =
      state.valuation.carDetails.averageValuation.trade &&
      state.valuation.carDetails.averageValuation.trade.amountGBP !== undefined
        ? state.valuation.carDetails.averageValuation.trade.amountGBP.toString()
        : '0';

    yield put({
      type: LOAD_VALUATION_SUCCEEDED,
      valuation: fullValuation
    });
  } catch (e) {
    const message = getErrorMessage(e);
    yield put({ type: LOAD_VALUATION_FAILED, message });
  }
}



// WEBPACK FOOTER //
// ./src/ducks/valuation/index.js